import { HIERARCHY_FIELD_MAPPING } from './constant';

export default class Helper {
    static usZipCodeRegex = new RegExp(/^\d{5}(-\d{4})?(?!-)$/);
    
    static isGeoInputValid = (geoLocation, geographyType, states) => {
        if (!geoLocation || !(geoLocation.zipCode || geoLocation.state)) 
            return false;
        const zipValid = Helper.usZipCodeRegex.test(geoLocation.zipCode);
        const stateValid = states?.some((st) => st.name?.toLowerCase() === geoLocation.state?.toLowerCase());
        const g = (geographyType || "").toLowerCase();
        if (g === "zip code only") return zipValid;
        if (g === "state only") return stateValid;
        return zipValid || stateValid;
    };

    static getGeoErrorMessage = (geographyType, geoInput) => {
        switch (geographyType.toLowerCase()) {
            case "zip code only":
                return "Please enter a valid ZIP Code.";
            case "state only":
                return "Please enter a valid State.";
            case "zip code or state":
                return /^[0-9\s]*$/.test(geoInput) 
                    ? "Please enter a valid ZIP Code." 
                    : /^[a-zA-Z\s]*$/.test(geoInput)
                        ? "Please enter a valid State."
                        : "Please enter a valid ZIP Code or State.";
            default:
                return "";
        }
    };
    
    // Streamlined side HTML configuration with inline ordering
    static getSideHtmlConfig = (config, isMobile) => {
        const {
            sideHtmlDesktopUrl,
            sideHtmlMobileUrl,
            sideHtmlDesktopPosition,
            sideHtmlMobilePosition,
            sideHtmlPercentWidth
        } = config;

        const htmlUrl = isMobile ? sideHtmlMobileUrl : sideHtmlDesktopUrl;
        const position = isMobile ? sideHtmlMobilePosition : sideHtmlDesktopPosition;
        
        if (!htmlUrl) {
            return {
                showHtml: false,
                containerClass: "",
                htmlUrl: "",
                width: "100%",
                htmlOrder: 1,
                contentOrder: 2
            };
        }

        // Determine order based on position
        let htmlOrder, contentOrder;
        
        if (isMobile) {
            // Mobile: top = HTML first (order 1), bottom = HTML second (order 2)
            const isTop = position?.toLowerCase().includes("top");
            htmlOrder = isTop ? 1 : 2;
            contentOrder = isTop ? 2 : 1;
        } else {
            // Desktop: left = HTML first (order 1), right = HTML second (order 2)
            const isRight = position?.toLowerCase().includes("right");
            htmlOrder = isRight ? 2 : 1;
            contentOrder = isRight ? 1 : 2;
        }

        const containerClass = Helper.getContainerClass(position, isMobile, true);
        const width = isMobile ? "auto" : `${sideHtmlPercentWidth || 24}%`;
        const contentWidth = isMobile ? "auto" : `${100-sideHtmlPercentWidth || 76}%`;

        return {
            showHtml: true,
            containerClass,
            htmlUrl,
            width,
            contentWidth,
            htmlOrder,
            contentOrder
        };
    };

    static getContainerClass = (position, isMobile, hasHtml) => {
        if (!hasHtml) return "";
        const positionClass = isMobile 
            ? (position?.toLowerCase().includes("top") ? "topCard" : "bottomCard")
            : (position?.toLowerCase().includes("right") ? "rightCard" : "leftCard");
        return `norstella-pf-maincontainer-${positionClass}`;
    };

    static resetFocusToElement = (elementRef) => {
      if (document?.activeElement) {
        document.activeElement.blur();
      }
      if (elementRef?.current) {
        elementRef.current.focus();
      }
    };

    static IsSearchRequired = (geographyType) => {
        switch (geographyType?.toLowerCase()) {
            case "zip code or state":
            case "state only":
            case "zip code only":
                return true;
            default:
                return false;
        }
    };

    static IsPlanSearchRequired = (geographyType) => {
        switch (geographyType?.toLowerCase()) {
            case "zip code or state":
            case "state only":
            case "zip code only":
            case "national-hidden from ui":
                return true;
            default:
                return false;
        }
    };

    static replacePlaceholderWithValues = (text, {result, channel, hierarchy, state} = {}) => {
        if (text === null || text === undefined) {
            return "";
        }

        return text
        .replace("[result]", result || "")
        .replace("[channel]", channel || "")
        .replace("[Hierarchy]", hierarchy || "")
        .replace("[hierarchy]", hierarchy.toLowerCase() || "")
        .replace("[HIERARCHY]", hierarchy.toUpperCase() || "")
        .replace("[state]", state || "");
    };

    static resolveGeoLocationValue = (geoLocation, states) => {
        const zipCodeValue = /^\d*$/.test(geoLocation) ? geoLocation : "";
        const stateValue = states.find((state) => state.name.toLowerCase() === geoLocation.toLowerCase()) ? geoLocation : "";
        return { geoInput: geoLocation, zipCode: zipCodeValue, state: stateValue };
    };

    static retrieveResultName = (option, resultHierarchyName) => {
        if (!option) return "";
        
        // Handle HierarchyOption response type (for national search)
        if (option.hierarchyOptionName) {
            return option.hierarchyOptionName;
        }
        // Handle regular coverage response type
        const fieldName = HIERARCHY_FIELD_MAPPING[resultHierarchyName?.toLowerCase()]?.ResultName || "PlanName";
        return option?.[fieldName];
    };
}
